import { createFileRoute, Link } from '@tanstack/react-router'
import { PrinterIcon, TargetIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModelVersionAuditLogs } from '@/api/audit-logs'
import { useBatches } from '@/api/batches'
import { useIdempotencyKey } from '@/api/idempotency'
import {
  useArchiveModelVersion,
  useModelVersions,
  useUnarchiveModelVersion,
  useUpdateModelVersion,
} from '@/api/model-versions'
import type { ModelVersion } from '@/api/model-versions'
import { useModel } from '@/api/models'
import { useWorkspace } from '@/api/workspace'
import { BatchStatusBadge } from '@/components/batches/batch-parts'
import { VoidedBadge } from '@/components/corrections/voided'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { modelVersionErrorMessage } from '@/components/model-versions/error-message'
import { VersionFiles } from '@/components/model-versions/version-files'
import { VersionBadges } from '@/components/model-versions/version-list'
import { NOTES_MAX, VersionForm } from '@/components/model-versions/version-form'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordHistory } from '@/components/record-history/record-history'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

// Model version detail (UI-SCREENS P-07, #101): the files (download; add and *remove file* for
// the Owner, 03f #104), the Recipe, notes and breaking flag; *edit Recipe* (audited) and
// *archive / un-archive version* for the Owner. "Batches using it" lists the version's Batches, each linking to F-06 (04j #136).
// The Owner also sees the History section (#105).
// The version is found by its number among the Model's versions.
export const Route = createFileRoute('/w/$slug/products/$productId/models/$modelId/versions/$versionNo')({
  component: ModelVersionDetail,
  staticData: { breadcrumb: 'modelVersions.detail.title' },
})

function ModelVersionDetail() {
  const { t } = useTranslation()
  const { slug, productId, modelId, versionNo } = Route.useParams()
  const model = useModel(slug, Number(modelId))
  const versions = useModelVersions(slug, Number(modelId))
  const [editing, setEditing] = useState(false)
  const [notice, setNotice] = useState('')
  const isOwner = useWorkspace(slug).data?.role === 'owner'

  if (model.isPending || versions.isPending) return <LoadingSkeleton variant="page" />
  const version = versions.data?.find((item) => String(item.version_no) === versionNo)
  if (model.isError || !model.data || String(model.data.product_id) !== productId || !version) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {versions.data && !version ? t('modelVersions.detail.notFound') : t('errors.generic')}
      </p>
    )
  }

  return (
    <section className="flex max-w-3xl flex-col gap-6">
      <Link
        to="/w/$slug/products/$productId/models/$modelId"
        params={{ slug, productId, modelId }}
        className="text-sm underline-offset-4 hover:underline"
      >
        {t('modelVersions.back')}
      </Link>
      <div className="flex flex-col gap-2">
        <h1 className="font-heading text-xl font-semibold">
          {t('modelVersions.detail.heading', { model: model.data.name, no: version.version_no })}
        </h1>
        <div className="flex flex-wrap gap-2">
          <VersionBadges version={version} />
        </div>
      </div>
      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {notice}
      </p>

      {/* Keyed by lock_version, so the field restarts from the saved notes after each change. */}
      <Notes key={version.lock_version} slug={slug} version={version} />

      <VersionFiles slug={slug} version={version} />

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-medium">{t('modelVersions.recipe.title')}</h2>
          {version.can.update_recipe && !editing ? (
            <Button variant="outline" className="h-11" onClick={() => setEditing(true)}>
              {t('modelVersions.detail.editRecipe')}
            </Button>
          ) : null}
        </div>
        {editing ? (
          <>
            <p className="text-sm text-muted-foreground">{t('modelVersions.detail.editRecipeHint')}</p>
            <VersionForm
              slug={slug}
              model={model.data}
              version={version}
              onCancel={() => setEditing(false)}
              onSaved={() => {
                setEditing(false)
                setNotice(t('modelVersions.detail.recipeSaved'))
              }}
            />
          </>
        ) : (
          <Recipe version={version} />
        )}
      </section>

      <VersionBatches slug={slug} versionId={version.id} />

      <VersionArchive slug={slug} version={version} onNotice={setNotice} />
      {isOwner ? <VersionHistory slug={slug} versionId={version.id} /> : null}
    </section>
  )
}

// The version's id is known only once the versions load, so its History loads in a child.
function VersionHistory({ slug, versionId }: { slug: string; versionId: number }) {
  const history = useModelVersionAuditLogs(slug, versionId, { enabled: true })
  return <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} />
}

// "Batches using it" (P-07, 04j #136): the version's Batches, newest first, with number, status,
// Machine, purpose, OK / failed once completed and date; voided ones stay listed, greyed.
function VersionBatches({ slug, versionId }: { slug: string; versionId: number }) {
  const { t } = useTranslation()
  const batches = useBatches(slug, { model_version_id: versionId })

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-medium">{t('modelVersions.detail.batches')}</h2>
      {batches.isPending ? (
        <LoadingSkeleton rows={3} />
      ) : batches.isError || !batches.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : batches.data.length === 0 ? (
        <RecordListEmpty>{t('modelVersions.detail.noBatches')}</RecordListEmpty>
      ) : (
        <RecordList>
          {batches.data.map((batch) => (
            <RecordListItem
              key={batch.id}
              voided={Boolean(batch.voided_at)}
              title={
                <RecordLink to="/w/$slug/batches/$batchId" params={{ slug, batchId: String(batch.id) }}>
                  {batch.batch_no}
                </RecordLink>
              }
              badges={
                <>
                  <BatchStatusBadge batch={batch} />
                  <VoidedBadge record={batch} />
                </>
              }
              meta={[
                { icon: PrinterIcon, label: t('batches.columns.machine'), value: batch.machine.name },
                { icon: TargetIcon, label: t('batches.columns.purpose'), value: t(`batches.purpose.${batch.purpose}`) },
              ]}
              figure={
                batch.status === 'completed'
                  ? t('batches.list.parts', { ok: batch.parts_ok ?? 0, failed: batch.parts_failed ?? 0 })
                  : undefined
              }
              caption={formatBusinessDate(batch.occurred_on)}
            />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function Recipe({ version }: { version: ModelVersion }) {
  const { t } = useTranslation()
  const rows: [string, string][] = [
    [t('modelVersions.recipe.partsPerPlate'), String(version.parts_per_plate)],
    [t('modelVersions.recipe.estMinutesPerPlate'), t('modelVersions.recipe.minutesUnit', { minutes: version.est_minutes_per_plate })],
    [t('modelVersions.recipe.profileName'), version.profile_name ?? t('modelVersions.recipe.empty')],
    [t('modelVersions.recipe.nozzle'), version.nozzle ?? t('modelVersions.recipe.empty')],
  ]

  return (
    <div className="flex flex-col gap-4">
      <dl className="grid gap-3 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-col">
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="font-medium">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">{t('modelVersions.recipe.slots')}</h3>
        <ul className="flex flex-col divide-y rounded-md border">
          {version.slots.map((slot) => (
            <li key={slot.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
              <div className="flex flex-col">
                <span className="font-medium">{slot.label}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {t('models.slots.key')}: {slot.key}
                  {slot.slot_archived_at ? ` · ${t('modelVersions.form.slotArchived')}` : ''}
                </span>
              </div>
              <span className="text-sm">{t('modelVersions.recipe.gramsUnit', { grams: slot.est_grams_per_plate })}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function Notes({ slug, version }: { slug: string; version: ModelVersion }) {
  const { t } = useTranslation()
  const updateVersion = useUpdateModelVersion()
  const [notes, setNotes] = useState(version.notes ?? '')
  const [editing, setEditing] = useState(false)

  function save() {
    updateVersion.mutate(
      {
        params: { path: { slug, id: version.id } },
        body: { model_version: { notes: notes.trim(), lock_version: version.lock_version } },
      },
      { onSuccess: () => setEditing(false) },
    )
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-medium">{t('modelVersions.detail.notes')}</h2>
        {version.can.update && !editing ? (
          <Button variant="outline" className="h-11" onClick={() => setEditing(true)}>
            {t('modelVersions.detail.editNotes')}
          </Button>
        ) : null}
      </div>
      {editing ? (
        <div className="flex flex-col gap-2">
          <Textarea
            rows={3}
            maxLength={NOTES_MAX}
            aria-label={t('modelVersions.detail.notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button className="h-11" disabled={updateVersion.isPending} onClick={save}>
              {t(updateVersion.isPending ? 'modelVersions.form.saving' : 'modelVersions.detail.saveNotes')}
            </Button>
            <Button variant="outline" className="h-11" onClick={() => setEditing(false)}>
              {t('modelVersions.form.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-line">{version.notes ?? t('modelVersions.detail.noNotes')}</p>
      )}
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {updateVersion.isError ? modelVersionErrorMessage(t, updateVersion.error) : ''}
      </p>
    </section>
  )
}

function VersionArchive({ slug, version, onNotice }: { slug: string; version: ModelVersion; onNotice: (notice: string) => void }) {
  const { t } = useTranslation()
  const archive = useArchiveModelVersion()
  const unarchive = useUnarchiveModelVersion()
  const archiveKey = useIdempotencyKey()
  const unarchiveKey = useIdempotencyKey()
  const [confirming, setConfirming] = useState<'archive' | 'unarchive' | null>(null)
  const busy = archive.isPending || unarchive.isPending
  const error = archive.error ?? unarchive.error

  function run(action: 'archive' | 'unarchive') {
    setConfirming(null)
    const key = action === 'archive' ? archiveKey : unarchiveKey
    const mutation = action === 'archive' ? archive : unarchive
    mutation.mutate(
      { params: { path: { slug, model_version_id: version.id }, header: key.headerFor({ id: version.id }) } },
      {
        onSuccess: () => onNotice(t(`modelVersions.archive.${action}d`, { no: version.version_no })),
        onSettled: (_data, mutationError) => key.settle(mutationError),
      },
    )
  }

  if (!version.can.archive && !version.can.unarchive) return null

  return (
    <div className="flex flex-col gap-2 border-t pt-4">
      <p className="text-sm text-muted-foreground">
        {t(version.can.archive ? 'modelVersions.archive.archiveHint' : 'modelVersions.archive.unarchiveHint')}
      </p>
      {version.can.archive ? (
        <Button
          variant="ghost"
          className="h-11 self-start text-destructive"
          disabled={busy}
          onClick={() => setConfirming('archive')}
        >
          {t('modelVersions.archive.archive')}
        </Button>
      ) : (
        <Button variant="outline" className="h-11 self-start" disabled={busy} onClick={() => setConfirming('unarchive')}>
          {t('modelVersions.archive.unarchive')}
        </Button>
      )}
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {error ? modelVersionErrorMessage(t, error) : ''}
      </p>

      {confirming ? (
        <AlertDialog open onOpenChange={(open) => (open ? undefined : setConfirming(null))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(
                  confirming === 'archive' ? 'modelVersions.archive.confirmTitle' : 'modelVersions.archive.confirmUnarchiveTitle',
                  { no: version.version_no },
                )}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(confirming === 'archive' ? 'modelVersions.archive.archiveHint' : 'modelVersions.archive.confirmUnarchiveBody')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-11">{t('modelVersions.archive.cancel')}</AlertDialogCancel>
              <AlertDialogAction className="h-11" onClick={() => run(confirming)}>
                {t('modelVersions.archive.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  )
}
